import { useState, useMemo, useCallback, useEffect } from "react";
import { Voucher } from "@/lib/accounting/types";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Bell, BellRing, MessageCircle, Send, Clock, CheckCircle2, AlertTriangle,
  Calendar, Phone, Settings, Play, Pause, Trash2, Eye, RefreshCw
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// ── Types ────────────────────────────────────────────────────────────────

export type ReminderTiming = "before_3" | "on_due" | "after_3" | "after_7" | "after_15";
export type ReminderStatus = "scheduled" | "sent" | "failed" | "skipped";

export interface PaymentReminder {
  id: string;
  voucherId: string;
  invoiceNumber: string;
  partyName: string;
  phoneNumber: string;
  amount: number;
  dueDate: string;
  timing: ReminderTiming;
  status: ReminderStatus;
  scheduledDate: string;
  sentAt?: string;
  message: string;
  createdAt: string;
}

export interface ReminderSchedule {
  id: string;
  voucherId: string;
  invoiceNumber: string;
  partyName: string;
  phoneNumber: string;
  amount: number;
  dueDate: string;
  isActive: boolean;
  timings: ReminderTiming[];
  createdAt: string;
}

const TIMING_CONFIG: Record<ReminderTiming, { label: string; days: number; icon: typeof Clock; color: string }> = {
  before_3: { label: "3 days before due", days: -3, icon: Bell, color: "text-primary bg-primary/10" },
  on_due: { label: "On due date", days: 0, icon: BellRing, color: "text-warning bg-warning/10" },
  after_3: { label: "3 days overdue", days: 3, icon: AlertTriangle, color: "text-destructive bg-destructive/10" },
  after_7: { label: "7 days overdue", days: 7, icon: AlertTriangle, color: "text-destructive bg-destructive/15" },
  after_15: { label: "15 days overdue", days: 15, icon: AlertTriangle, color: "text-destructive bg-destructive/20" },
};

const COMPANY_NAME = "ABC Trading Co.";
const UPI_ID = "business@upi";

// ── Helpers ──────────────────────────────────────────────────────────────

function getDueDate(invoiceDate: string): string {
  const d = new Date(invoiceDate);
  d.setDate(d.getDate() + 30);
  return d.toISOString().split("T")[0];
}

function getReminderDate(dueDate: string, timing: ReminderTiming): string {
  const d = new Date(dueDate);
  d.setDate(d.getDate() + TIMING_CONFIG[timing].days);
  return d.toISOString().split("T")[0];
}

function getDaysDiff(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function buildReminderMessage(
  partyName: string, amount: number, dueDate: string,
  timing: ReminderTiming, invoiceNumber: string
): string {
  const amtStr = `₹${amount.toLocaleString("en-IN")}`;
  const dueDateStr = new Date(dueDate).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  const daysDiff = getDaysDiff(dueDate);

  let urgency = "";
  if (timing === "before_3") {
    urgency = `Your payment is due in 3 days.`;
  } else if (timing === "on_due") {
    urgency = `Your payment is *due today*.`;
  } else if (timing === "after_3") {
    urgency = `⚠️ Your payment is *3 days overdue*. Please pay immediately.`;
  } else if (timing === "after_7") {
    urgency = `🚨 Your payment is *7 days overdue*. Immediate action required.`;
  } else if (timing === "after_15") {
    urgency = `🚨🚨 Your payment is *15 days overdue*. This is a final reminder.`;
  }

  return `Hello${partyName ? ` *${partyName}*` : ""},

*Payment Reminder* from *${COMPANY_NAME}*

📄 *Invoice:* ${invoiceNumber}
💰 *Amount Due:* ${amtStr}
📅 *Due Date:* ${dueDateStr}

${urgency}

💳 *Pay via UPI:* ${UPI_ID}

Please make the payment at your earliest convenience.

Thank you! 🙏`;
}

function formatIndianPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.startsWith("91") && cleaned.length === 12) return cleaned;
  if (cleaned.length === 10) return `91${cleaned}`;
  return cleaned;
}

function isValidIndianPhone(phone: string): boolean {
  const cleaned = phone.replace(/\D/g, "");
  return (cleaned.length === 10 && /^[6-9]/.test(cleaned)) ||
    (cleaned.length === 12 && cleaned.startsWith("91") && /^91[6-9]/.test(cleaned));
}

// ── Storage ──────────────────────────────────────────────────────────────

function getStoredSchedules(): ReminderSchedule[] {
  return JSON.parse(localStorage.getItem("wa_reminder_schedules") || "[]");
}

function saveSchedules(schedules: ReminderSchedule[]) {
  localStorage.setItem("wa_reminder_schedules", JSON.stringify(schedules));
}

function getStoredReminders(): PaymentReminder[] {
  return JSON.parse(localStorage.getItem("wa_payment_reminders") || "[]");
}

function saveReminders(reminders: PaymentReminder[]) {
  localStorage.setItem("wa_payment_reminders", JSON.stringify(reminders));
}

// ── Setup Reminders Dialog ───────────────────────────────────────────────

interface SetupRemindersDialogProps {
  voucher: Voucher;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScheduleCreated: () => void;
}

export function SetupRemindersDialog({ voucher, open, onOpenChange, onScheduleCreated }: SetupRemindersDialogProps) {
  const { toast } = useToast();
  const [phone, setPhone] = useState(voucher.partyName ? "" : "");
  const [timings, setTimings] = useState<ReminderTiming[]>(["before_3", "on_due", "after_3"]);
  const dueDate = getDueDate(voucher.date);

  const toggleTiming = (t: ReminderTiming) => {
    setTimings((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
  };

  const handleSave = () => {
    if (!isValidIndianPhone(phone)) {
      toast({ title: "Invalid Phone", description: "Enter a valid 10-digit Indian mobile number", variant: "destructive" });
      return;
    }
    if (timings.length === 0) {
      toast({ title: "No Timings Selected", description: "Select at least one reminder timing", variant: "destructive" });
      return;
    }

    const schedule: ReminderSchedule = {
      id: `sched-${crypto.randomUUID().slice(0, 8)}`,
      voucherId: voucher.id,
      invoiceNumber: voucher.invoiceNumber || voucher.voucherNumber,
      partyName: voucher.partyName || "Customer",
      phoneNumber: phone,
      amount: voucher.totalDebit,
      dueDate,
      isActive: true,
      timings,
      createdAt: new Date().toISOString(),
    };

    // Create individual reminders
    const reminders: PaymentReminder[] = timings.map((timing) => ({
      id: `rem-${crypto.randomUUID().slice(0, 8)}`,
      voucherId: voucher.id,
      invoiceNumber: schedule.invoiceNumber,
      partyName: schedule.partyName,
      phoneNumber: phone,
      amount: voucher.totalDebit,
      dueDate,
      timing,
      status: "scheduled" as ReminderStatus,
      scheduledDate: getReminderDate(dueDate, timing),
      message: buildReminderMessage(schedule.partyName, voucher.totalDebit, dueDate, timing, schedule.invoiceNumber),
      createdAt: new Date().toISOString(),
    }));

    const existingSchedules = getStoredSchedules();
    saveSchedules([schedule, ...existingSchedules]);

    const existingReminders = getStoredReminders();
    saveReminders([...reminders, ...existingReminders]);

    toast({ title: "Reminders Scheduled", description: `${timings.length} reminders set for ${schedule.invoiceNumber}` });
    onScheduleCreated();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BellRing className="h-5 w-5 text-success" />
            Setup Payment Reminders
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Invoice Info */}
          <div className="bg-muted/50 rounded-lg p-3 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Invoice</span>
              <span className="text-sm font-medium text-foreground">{voucher.invoiceNumber || voucher.voucherNumber}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Party</span>
              <span className="text-sm text-foreground">{voucher.partyName || "—"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Amount</span>
              <span className="text-sm font-bold text-foreground">₹{voucher.totalDebit.toLocaleString("en-IN")}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Due Date</span>
              <span className="text-sm text-foreground">{new Date(dueDate).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Customer WhatsApp Number</label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">+91</span>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="9876543210"
                className="flex-1"
              />
            </div>
          </div>

          {/* Timing Selection */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-2 block">Reminder Schedule</label>
            <div className="space-y-2">
              {(Object.entries(TIMING_CONFIG) as [ReminderTiming, typeof TIMING_CONFIG[ReminderTiming]][]).map(([key, config]) => {
                const date = getReminderDate(dueDate, key);
                const isPast = getDaysDiff(date) < 0;
                return (
                  <label
                    key={key}
                    className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors cursor-pointer ${
                      timings.includes(key) ? "border-accent bg-accent/5" : "border-border bg-card"
                    } ${isPast ? "opacity-50" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={timings.includes(key)}
                      onChange={() => toggleTiming(key)}
                      className="sr-only"
                    />
                    <div className={`h-7 w-7 rounded-full flex items-center justify-center ${config.color}`}>
                      <config.icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="flex-1">
                      <span className="text-sm font-medium text-foreground">{config.label}</span>
                      <span className="text-xs text-muted-foreground block">
                        {new Date(date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        {isPast && " (past)"}
                      </span>
                    </div>
                    <div className={`h-4 w-4 rounded border-2 flex items-center justify-center ${
                      timings.includes(key) ? "bg-accent border-accent" : "border-muted-foreground/30"
                    }`}>
                      {timings.includes(key) && <CheckCircle2 className="h-3 w-3 text-accent-foreground" />}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Preview */}
          {timings.length > 0 && (
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Message Preview (first reminder)</label>
              <div className="bg-[hsl(var(--muted))] rounded-lg p-3 text-xs text-foreground whitespace-pre-wrap max-h-32 overflow-y-auto font-mono">
                {buildReminderMessage(voucher.partyName || "Customer", voucher.totalDebit, dueDate, timings[0], voucher.invoiceNumber || voucher.voucherNumber)}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} className="gap-1.5 bg-success hover:bg-success/90 text-white">
            <BellRing className="h-4 w-4" />
            Schedule {timings.length} Reminder{timings.length !== 1 ? "s" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Reminder Dashboard Panel ─────────────────────────────────────────────

interface ReminderDashboardProps {
  vouchers: Voucher[];
}

export function ReminderDashboard({ vouchers }: ReminderDashboardProps) {
  const { toast } = useToast();
  const [reminders, setReminders] = useState<PaymentReminder[]>(getStoredReminders);
  const [schedules, setSchedules] = useState<ReminderSchedule[]>(getStoredSchedules);
  const [filter, setFilter] = useState<"all" | "due_today" | "overdue" | "sent">("all");

  const refresh = useCallback(() => {
    setReminders(getStoredReminders());
    setSchedules(getStoredSchedules());
  }, []);

  const today = new Date().toISOString().split("T")[0];

  const stats = useMemo(() => {
    const scheduled = reminders.filter((r) => r.status === "scheduled");
    const dueToday = scheduled.filter((r) => r.scheduledDate === today);
    const overdue = scheduled.filter((r) => r.scheduledDate < today);
    const sent = reminders.filter((r) => r.status === "sent");
    return { total: reminders.length, scheduled: scheduled.length, dueToday: dueToday.length, overdue: overdue.length, sent: sent.length };
  }, [reminders, today]);

  const filtered = useMemo(() => {
    switch (filter) {
      case "due_today": return reminders.filter((r) => r.status === "scheduled" && r.scheduledDate === today);
      case "overdue": return reminders.filter((r) => r.status === "scheduled" && r.scheduledDate < today);
      case "sent": return reminders.filter((r) => r.status === "sent");
      default: return reminders;
    }
  }, [reminders, filter, today]);

  const sendReminder = useCallback((reminder: PaymentReminder) => {
    const formattedPhone = formatIndianPhone(reminder.phoneNumber);
    const encoded = encodeURIComponent(reminder.message);
    const url = `https://wa.me/${formattedPhone}?text=${encoded}`;
    window.open(url, "_blank");

    const updated = reminders.map((r) =>
      r.id === reminder.id ? { ...r, status: "sent" as ReminderStatus, sentAt: new Date().toISOString() } : r
    );
    setReminders(updated);
    saveReminders(updated);
    toast({ title: "WhatsApp Opened", description: `Reminder for ${reminder.invoiceNumber} ready to send` });
  }, [reminders, toast]);

  const sendAllDue = useCallback(() => {
    const dueReminders = reminders.filter((r) => r.status === "scheduled" && r.scheduledDate <= today);
    if (dueReminders.length === 0) {
      toast({ title: "No Due Reminders", description: "No reminders are due today" });
      return;
    }
    // Send first one, mark all as sent
    if (dueReminders[0]) sendReminder(dueReminders[0]);
    const ids = new Set(dueReminders.map((r) => r.id));
    const updated = reminders.map((r) =>
      ids.has(r.id) ? { ...r, status: "sent" as ReminderStatus, sentAt: new Date().toISOString() } : r
    );
    setReminders(updated);
    saveReminders(updated);
    toast({ title: "Reminders Sent", description: `${dueReminders.length} reminders processed` });
  }, [reminders, today, sendReminder, toast]);

  const deleteSchedule = useCallback((scheduleId: string) => {
    const schedule = schedules.find((s) => s.id === scheduleId);
    if (!schedule) return;
    const updatedSchedules = schedules.filter((s) => s.id !== scheduleId);
    const updatedReminders = reminders.filter((r) => r.voucherId !== schedule.voucherId || r.status === "sent");
    setSchedules(updatedSchedules);
    setReminders(updatedReminders);
    saveSchedules(updatedSchedules);
    saveReminders(updatedReminders);
    toast({ title: "Schedule Removed", description: `Reminders for ${schedule.invoiceNumber} cancelled` });
  }, [schedules, reminders, toast]);

  if (reminders.length === 0 && schedules.length === 0) {
    return (
      <div className="text-center py-8">
        <BellRing className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
        <p className="text-muted-foreground text-sm font-medium">No payment reminders set</p>
        <p className="text-xs text-muted-foreground mt-1">Set up reminders from unpaid invoices to auto-send WhatsApp messages</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total Reminders", value: stats.total, icon: Bell, color: "text-accent", filter: "all" as const },
          { label: "Due Today", value: stats.dueToday, icon: BellRing, color: "text-warning", filter: "due_today" as const },
          { label: "Overdue", value: stats.overdue, icon: AlertTriangle, color: "text-destructive", filter: "overdue" as const },
          { label: "Sent", value: stats.sent, icon: CheckCircle2, color: "text-success", filter: "sent" as const },
        ].map((s) => (
          <button
            key={s.label}
            onClick={() => setFilter(s.filter)}
            className={`bg-card border rounded-lg p-3 text-left transition-colors ${
              filter === s.filter ? "border-accent" : "border-border hover:border-accent/50"
            }`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <s.icon className={`h-3.5 w-3.5 ${s.color}`} />
              <span className="text-xs text-muted-foreground">{s.label}</span>
            </div>
            <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
          </button>
        ))}
      </div>

      {/* Action Bar */}
      {stats.dueToday + stats.overdue > 0 && (
        <div className="flex items-center justify-between bg-warning/10 border border-warning/30 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <BellRing className="h-4 w-4 text-warning" />
            <span className="text-sm font-medium text-foreground">
              {stats.dueToday + stats.overdue} reminder{stats.dueToday + stats.overdue !== 1 ? "s" : ""} ready to send
            </span>
          </div>
          <Button size="sm" onClick={sendAllDue} className="gap-1.5 bg-success hover:bg-success/90 text-white">
            <Send className="h-3.5 w-3.5" /> Send All Due
          </Button>
        </div>
      )}

      {/* Reminder List */}
      <div className="space-y-2">
        {filtered.map((r) => {
          const tc = TIMING_CONFIG[r.timing];
          const daysDiff = getDaysDiff(r.scheduledDate);
          const isDue = r.status === "scheduled" && daysDiff <= 0;

          return (
            <div key={r.id} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:bg-muted/30 transition-colors">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${tc.color}`}>
                <tc.icon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-foreground">{r.invoiceNumber}</span>
                  <Badge variant="outline" className="text-xs">₹{r.amount.toLocaleString("en-IN")}</Badge>
                  <Badge className={`text-xs border-0 ${tc.color}`}>{tc.label}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {r.partyName} • {r.phoneNumber} • {new Date(r.scheduledDate).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                  {r.sentAt && ` • Sent ${new Date(r.sentAt).toLocaleString("en-IN")}`}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {r.status === "scheduled" && isDue && (
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Send Now" onClick={() => sendReminder(r)}>
                    <Send className="h-3.5 w-3.5 text-success" />
                  </Button>
                )}
                {r.status === "sent" && <CheckCircle2 className="h-4 w-4 text-success" />}
                {r.status === "scheduled" && !isDue && <Clock className="h-4 w-4 text-muted-foreground" />}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-6">
            <p className="text-sm text-muted-foreground">No reminders in this view</p>
          </div>
        )}
      </div>

      {/* Active Schedules */}
      {schedules.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Active Schedules</h4>
          <div className="space-y-2">
            {schedules.map((s) => (
              <div key={s.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-border/50 bg-muted/30">
                <Calendar className="h-4 w-4 text-accent shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-foreground">{s.invoiceNumber}</span>
                  <span className="text-xs text-muted-foreground block">
                    {s.partyName} • {s.timings.length} reminders • Due {new Date(s.dueDate).toLocaleDateString("en-IN")}
                  </span>
                </div>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Remove" onClick={() => deleteSchedule(s.id)}>
                  <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Quick Reminder Button (for invoice rows) ─────────────────────────────

interface ReminderQuickButtonProps {
  voucher: Voucher;
  onScheduleCreated?: () => void;
}

export function ReminderQuickButton({ voucher, onScheduleCreated }: ReminderQuickButtonProps) {
  const [open, setOpen] = useState(false);
  const existing = getStoredSchedules().some((s) => s.voucherId === voucher.id);

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        title={existing ? "Reminders Active" : "Setup Reminders"}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
      >
        {existing
          ? <BellRing className="h-3.5 w-3.5 text-warning" />
          : <Bell className="h-3.5 w-3.5 text-muted-foreground" />
        }
      </Button>
      {open && (
        <SetupRemindersDialog
          voucher={voucher}
          open={open}
          onOpenChange={setOpen}
          onScheduleCreated={onScheduleCreated || (() => {})}
        />
      )}
    </>
  );
}
